import React, { useState } from 'react';
import { ArrowLeft, ShoppingBag, X, Minus, Plus } from 'lucide-react';
import { Store, Product, CartItem } from '../../types';
import { PRODUCTS } from '../../data/mockData';
import { ProductCard } from '../ProductCard';
import { Button } from '../ui/Button';
import { useToast } from '../ToastContext';
// Indian Rupee formatting helper
const formatINR = (amount: number): string => {
  return `₹${Math.round(amount).toLocaleString('en-IN')}`;
};
interface MenuScreenProps {
  store: Store;
  onBack: () => void;
  onCheckout: (items: CartItem[]) => void;
}
export function MenuScreen({
  store,
  onBack,
  onCheckout
}: MenuScreenProps) {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState('All');
  const {
    showToast
  } = useToast();
  const products = PRODUCTS[store.id] || PRODUCTS['1'];
  const categories = ['All', ...Array.from(new Set(products.map(p => p.category)))];
  const filteredProducts = activeCategory === 'All' ? products : products.filter(p => p.category === activeCategory);
  const addToCart = (product: Product) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item => item.id === product.id ? {
          ...item,
          quantity: item.quantity + 1
        } : item);
      }
      return [...prev, {
        ...product,
        quantity: 1
      }];
    });
    showToast(`Added ${product.name} to cart`, 'success');
  };
  const updateQuantity = (id: string, delta: number) => {
    setCart(prev => prev.map(item => {
      if (item.id === id) {
        return {
          ...item,
          quantity: Math.max(0, item.quantity + delta)
        };
      }
      return item;
    }).filter(item => item.quantity > 0));
  };
  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  return <div className="pb-24 pt-6 h-full flex flex-col bg-gray-50 animate-in slide-in-from-right duration-300" data-id="element-1307">
      <div className="px-4 mb-6 flex items-center justify-between" data-id="element-1308">
        <div className="flex items-center gap-3" data-id="element-1309">
          <button onClick={onBack} className="p-2 bg-white rounded-full shadow-sm hover:bg-gray-50" data-id="element-1310">
            <ArrowLeft size={20} data-id="element-1311" />
          </button>
          <div data-id="element-1312">
            <h1 className="text-xl font-bold text-gray-900" data-id="element-1313">{store.name}</h1>
            <p className="text-xs text-gray-500" data-id="element-1314">Menu</p>
          </div>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-4 px-4 scrollbar-hide mb-2" data-id="element-1315">
        {categories.map(cat => <button key={cat} onClick={() => setActiveCategory(cat)} className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${activeCategory === cat ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 border border-gray-200'}`} data-id="element-1316">
            {cat}
          </button>)}
      </div>

      <div className="flex-1 overflow-y-auto px-4 pb-24" data-id="element-1317">
        <div className="grid grid-cols-2 gap-4" data-id="element-1318">
          {filteredProducts.map(product => <ProductCard key={product.id} product={product} onAdd={() => addToCart(product)} data-id="element-1319" />)}
        </div>
      </div>

      {cartCount > 0 && <div className="fixed bottom-6 left-4 right-4 z-30 max-w-md mx-auto" data-id="element-1320">
          <button onClick={() => setIsCartOpen(true)} className="w-full bg-purple-600 text-white p-4 rounded-2xl shadow-xl shadow-purple-600/30 flex items-center justify-between active:scale-95 transition-all" data-id="element-1321">
            <div className="flex items-center gap-3" data-id="element-1322">
              <div className="bg-white/20 px-3 py-1 rounded-lg font-bold text-sm" data-id="element-1323">
                {cartCount}
              </div>
              <span className="font-bold" data-id="element-1324">View Cart</span>
            </div>
            <span className="font-bold" data-id="element-1325">{formatINR(cartTotal)}</span>
          </button>
        </div>}

      {isCartOpen && <>
          <div className="fixed inset-0 bg-black/40 z-40 backdrop-blur-sm animate-in fade-in" onClick={() => setIsCartOpen(false)} data-id="element-1326" />
          <div className="fixed bottom-0 left-0 right-0 bg-white z-50 rounded-t-3xl p-6 max-w-md mx-auto animate-in slide-in-from-bottom duration-300" data-id="element-1327">
            <div className="flex justify-between items-center mb-6" data-id="element-1328">
              <h2 className="text-xl font-bold" data-id="element-1329">Your Order</h2>
              <button onClick={() => setIsCartOpen(false)} className="p-2 bg-gray-100 rounded-full" data-id="element-1330">
                <X size={20} data-id="element-1331" />
              </button>
            </div>

            <div className="space-y-4 max-h-[40vh] overflow-y-auto mb-6" data-id="element-1332">
              {cart.map(item => <div key={item.id} className="flex items-center justify-between" data-id="element-1333">
                  <div className="flex items-center gap-3" data-id="element-1334">
                    <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden" data-id="element-1335">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-1336" />
                    </div>
                    <div data-id="element-1337">
                      <p className="font-bold text-sm" data-id="element-1338">{item.name}</p>
                      <p className="text-xs text-gray-500" data-id="element-1339">
                        {formatINR(item.price)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-1" data-id="element-1340">
                    <button onClick={() => updateQuantity(item.id, -1)} className="p-1 hover:bg-white rounded-md shadow-sm transition-all" data-id="element-1341">
                      <Minus size={14} data-id="element-1342" />
                    </button>
                    <span className="text-sm font-bold w-4 text-center" data-id="element-1343">
                      {item.quantity}
                    </span>
                    <button onClick={() => updateQuantity(item.id, 1)} className="p-1 hover:bg-white rounded-md shadow-sm transition-all" data-id="element-1344">
                      <Plus size={14} data-id="element-1345" />
                    </button>
                  </div>
                </div>)}
            </div>

            <div className="border-t border-gray-100 pt-4 mb-6" data-id="element-1346">
              <div className="flex justify-between items-center mb-2" data-id="element-1347">
                <span className="text-gray-500" data-id="element-1348">Subtotal</span>
                <span className="font-bold" data-id="element-1349">{formatINR(cartTotal)}</span>
              </div>
              <div className="flex justify-between items-center text-lg font-bold" data-id="element-1350">
                <span data-id="element-1351">Total</span>
                <span data-id="element-1352">{formatINR(cartTotal)}</span>
              </div>
            </div>

            <Button fullWidth size="lg" onClick={() => onCheckout(cart)} data-id="element-1353">
              Checkout
            </Button>
          </div>
        </>}
    </div>;
}